import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  Signal,
  DestroyRef,
  input,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed, toSignal, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, switchMap, take } from 'rxjs/operators';

import { CurrentSiteState } from '@ppg/configuration';
import { GridPaginatorRows } from '@ppg/core/constants';
import { ClientSiteSettingsKeys, ContentComponentType, ResourceItem } from '@ppg/core/enums';
import {
  Option,
  ColorFamily,
  ColorInfoValid,
  Content,
  ComponentContentBannerContent,
  ThemeColor,
  ColorFamiliesPageContent,
  Color,
  NavigationStateOptions,
} from '@ppg/core/models';
import { GoogleAnalyticsPagePrefix } from '@ppg/shared/analytics';
import { BreadcrumbsComponent } from '@ppg/shared/breadcrumbs';
import { ColorsGridComponent } from '@ppg/shared/color/colors-grid';
import { ColorGtmService } from '@ppg/shared/color/services';
import { ContentBannerComponent, HeroBannerComponent } from '@ppg/shared/components';
import { appendImageSize } from '@ppg/shared/product-api';
import { ResourceService } from '@ppg/shared/resource';
import { UrlFormatter } from '@ppg/shared/utils';

import { ColorFamilyFocusRegion } from '../../enums/color-family-focus-region.enum';
import { ColorInfoGroup } from '../../models/color-group.model';
import { ColorFamilyService } from '../../services/color-family.service';
import { ColorFamilyGroupingComponent } from '../color-family-grouping/color-family-grouping.component';
import { ColorFamilySwitchersGroupedComponent } from '../color-family-switchers-grouped/color-family-switchers-grouped.component';

@Component({
  selector: 'ppg-color-families',
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    ColorFamilySwitchersGroupedComponent,
    ColorFamilyGroupingComponent,
    ColorsGridComponent,
    ContentBannerComponent,
    HeroBannerComponent,
  ],
  templateUrl: './color-families.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorFamiliesComponent implements OnInit, AfterViewInit {
  readonly familySwitcherId = ColorFamilyFocusRegion.FamilySwitcher;
  readonly familyDescriptionId = ColorFamilyFocusRegion.familyDescription;

  readonly #colorFamilyService = inject(ColorFamilyService);
  readonly #currentSiteState = inject(CurrentSiteState);
  readonly #destroyRef = inject(DestroyRef);
  readonly #resourceService = inject(ResourceService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #location = inject(Location);
  readonly #urlFormatter = inject(UrlFormatter);
  readonly #colorGtmService = inject(ColorGtmService);

  readonly #paramMap = toSignal(this.#activatedRoute.paramMap);
  readonly #routeFamilyName = computed<string>(() => {
    const familyName = this.#paramMap()?.get('familyName');
    return this.#urlFormatter.formatUrlPathToStringValue(familyName);
  });
  readonly #routeFamilyTypeName = computed<string>(() => {
    const familyTypeName = this.#paramMap()?.get('familyTypeName');
    return this.#urlFormatter.formatUrlPathToStringValue(familyTypeName);
  });

  readonly content = input.required<Content>();
  readonly #pageContent: Signal<ColorFamiliesPageContent> = computed(() => this.content().page.content);
  readonly heroBannerContent = computed(() => this.#pageContent().heroBanner?.content);
  readonly launchColorVisualizerButtonContent = computed(() => this.#pageContent().ctaLaunchColorVisualizer);
  readonly descriptionImagePosition = computed(() => this.#pageContent().descriptionImagePosition);

  readonly breadcrumbHomeCodename = this.#currentSiteState.getSiteSettingValueByKey(
    ClientSiteSettingsKeys.FeatureBreadcrumbsHome,
  );
  readonly breadcrumbsHome = computed(() => this.content()?.resources[this.breadcrumbHomeCodename]);
  readonly breadcrumbs = computed(() => {
    const colorFamilyName = this.#getFamilyObject(this.selectedColorFamily())?.name;

    if (!colorFamilyName) {
      return;
    }

    const breadcrumbs = this.content()?.breadcrumbs;

    const lastItem = breadcrumbs[breadcrumbs.length - 1];
    if (!lastItem.url) {
      lastItem.url = this.getUrlWithoutParams(colorFamilyName);
    }

    const colorFamilyNameBreacrumb = {
      title: colorFamilyName,
      dataTestId: 'colorFamilies.selectedFamilyName',
    };
    return [...breadcrumbs, colorFamilyNameBreacrumb];
  });

  readonly resources = {
    sectionHeader: this.#resourceService.getResourceSignal(ResourceItem.COLORS_FAMILIES_SHADOWS_SECTION_HEADER),
  };

  readonly familiesGroupedByType = this.#colorFamilyService.colorFamiliesGroupedByType;
  readonly selectedColorFamily = computed(() => {
    const familyName = this.#routeFamilyName();
    const familiesGroupedByType = this.familiesGroupedByType();

    if (!familyName) {
      return;
    }

    let selectedFamily;
    let selectedFamilyTypeKey;

    if (!this.#withFamilyTypeGrouping()) {
      selectedFamily = Object.values(familiesGroupedByType)[0]?.find(
        (family) => family.name.toLowerCase() === familyName.toLowerCase(),
      );
      selectedFamilyTypeKey = Object.keys(familiesGroupedByType)[0];

      return selectedFamilyTypeKey ? { [selectedFamilyTypeKey]: selectedFamily } : undefined;
    }

    const familyGroupName = this.#routeFamilyTypeName();

    if (!familyGroupName) {
      return;
    }

    selectedFamily = familiesGroupedByType[familyGroupName]?.find(
      (family) => family.name.toLowerCase() === familyName.toLowerCase(),
    );
    selectedFamilyTypeKey = familyGroupName;

    return selectedFamilyTypeKey ? { [selectedFamilyTypeKey]: selectedFamily } : undefined;
  });

  readonly selectedFamilyId$ = toObservable(computed(() => this.#getFamilyObject(this.selectedColorFamily())?.id));

  readonly colorFamilyGroups = signal<ColorInfoGroup[] | null>(null);
  readonly colorsPerFamily = signal<ColorInfoValid[] | null>(null);
  readonly groupingEnabled = this.#colorFamilyService.isFamilyGroupingEnabled();
  readonly paginatorHidden = this.#colorFamilyService.isPaginatorHidden();
  readonly defaultRowsPerPage = GridPaginatorRows.twenty;
  readonly familySwitcherStyle = computed(() => {
    const buttonsPerRow = Number(
      this.#currentSiteState.getSiteSettingValueByKey(ClientSiteSettingsKeys.ColorJourneyFamilySwitcherButtonsPerRow),
    );
    return this.getFamilySwitcherStyle(buttonsPerRow);
  });

  readonly familyDescription = computed(() => {
    if (!this.selectedColorFamily()) {
      return undefined;
    }

    const selectedFamily = this.#getFamilyObject(this.selectedColorFamily());

    const launchColorVisualizerButtonContent = this.launchColorVisualizerButtonContent();

    return {
      showLogo: false,
      headline: selectedFamily?.name,
      image: selectedFamily?.description?.imageUrl
        ? {
            url: appendImageSize(selectedFamily?.description?.imageUrl),
            description: undefined,
            imagePosition: this.descriptionImagePosition(),
          }
        : undefined,
      content: [{ content: selectedFamily?.description?.content, type: ContentComponentType.RICH_TEXT }],
      colors: {
        backgroundColor: ThemeColor.SECONDARY,
        textColor: ThemeColor.PRIMARY,
      },
      cta: launchColorVisualizerButtonContent,
    } as unknown as ComponentContentBannerContent;
  });

  constructor() {
    toObservable(this.selectedColorFamily)
      .pipe(filter(this.#filterOutUndefinedFamily), take(1))
      .subscribe((family) =>
        this.#colorGtmService.pushColorFamilyGTMTag(
          'color_family_view',
          this.#getFamilyObject(family)?.name,
          this.#getFamilyObject(family)?.typeLabel,
        ),
      );
  }

  ngOnInit(): void {
    this.selectedFamilyId$
      .pipe(
        filter((selectedFamilyId): selectedFamilyId is string => !!selectedFamilyId),
        switchMap((selectedFamilyId) => {
          if (this.groupingEnabled) {
            return this.#colorFamilyService.getGroupedColorsPerFamily(selectedFamilyId);
          } else {
            return this.#colorFamilyService.getColorsPerFamily(selectedFamilyId);
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((result) => {
        if (this.groupingEnabled) {
          this.colorFamilyGroups.set(result as ColorInfoGroup[]);
        } else {
          this.colorsPerFamily.set(result as ColorInfoValid[]);
        }
      });
  }

  ngAfterViewInit(): void {
    const routeState = this.#location.getState() as NavigationStateOptions | undefined;

    if (routeState?.shouldScroll && routeState?.fragment) {
      const navigationPath = this.#selectedColorFamilyNavigationPath(
        this.#routeFamilyName(),
        this.#routeFamilyTypeName(),
      );

      this.#router.navigate([navigationPath], { fragment: routeState.fragment, relativeTo: this.#activatedRoute });
    }
  }

  onLaunchColorVisualizerClicked() {
    this.#colorGtmService.pushColorFamilyGTMTag(
      'color_family_launch_visualizer',
      this.#getFamilyObject(this.selectedColorFamily())?.name,
      this.#getFamilyObject(this.selectedColorFamily())?.typeLabel,
    );
  }

  onColorFamilySelected(
    colorFamilyWithGroupIdentifier: Record<string, ColorFamily>,
    shouldSendEvent: boolean = false,
  ): void {
    const familyTypeKey = Object.keys(colorFamilyWithGroupIdentifier)[0];
    const colorFamily = Object.values(colorFamilyWithGroupIdentifier)[0];

    if (!familyTypeKey || !colorFamily || !this.#selectedColorChanged(colorFamily, familyTypeKey)) {
      return;
    }

    if (shouldSendEvent) {
      this.#colorGtmService.pushColorFamilyGTMTag('color_family_click_to_family', colorFamily.name, familyTypeKey);
    }

    const navigationPath = this.#selectedColorFamilyNavigationPath(colorFamily?.name, familyTypeKey);

    this.#navigateToPath(navigationPath);
  }

  onColorInfoGroupChanged(colorInfoGroup: ColorInfoGroup | null) {
    this.#colorGtmService.pushColorFamilyWithShadeGTMTag(
      'color_family_change_shadow',
      this.#getFamilyObject(this.selectedColorFamily())?.name,
      this.#getFamilyObject(this.selectedColorFamily())?.typeLabel,
      colorInfoGroup ? this.#resourceService.getResourceByKey(colorInfoGroup?.labelResourceKey) : undefined,
    );
  }

  onFavoriteColorAdded(color: Color) {
    this.#colorGtmService.addColorToFavoritesGTMtag(color, GoogleAnalyticsPagePrefix.ColorFamilyPage);
  }

  onFavoriteColorRemoved(color: Color) {
    this.#colorGtmService.removeColorFromFavoritesGTMtag(color, GoogleAnalyticsPagePrefix.ColorFamilyPage);
  }

  onColorDetailsLinkClicked(color: Color) {
    this.#colorGtmService.pushColorDetailsGTMTag('color_family_click_cdp', color);
  }

  protected filterOptions: Signal<Option[]> = computed(() => {
    return (
      this.colorFamilyGroups()
        ?.filter((group) => group.colors.length > 0)
        .map((group, index) => ({
          id: index.toString(),
          label: this.#resourceService.getResourceByKey(group.labelResourceKey),
        })) ?? []
    );
  });

  protected getUrlWithoutParams(colorFamilyName: string) {
    const urlTree = this.#router.parseUrl(this.#router.url);
    urlTree.queryParams = {};

    return colorFamilyName ? urlTree.toString().replace(colorFamilyName, '') : urlTree.toString();
  }

  protected getFamilySwitcherStyle(buttonsPerRow: number) {
    switch (buttonsPerRow) {
      case 4:
        return 'col-6 sm:col-3 md:col-3';
      case 6:
        return 'col-6 sm:col-3 md:col-2';
      default:
        return;
    }
  }

  #selectedColorChanged(colorFamily: ColorFamily, familyTypeKey: string | undefined): boolean {
    if (!this.#withFamilyTypeGrouping()) {
      return colorFamily.name !== this.#routeFamilyName();
    }

    if (familyTypeKey === this.#routeFamilyTypeName()) {
      return colorFamily.name !== this.#routeFamilyName();
    } else if (familyTypeKey !== this.#routeFamilyTypeName() && colorFamily.name === this.#routeFamilyName()) {
      return true;
    }

    return colorFamily.name !== this.#routeFamilyName();
  }

  #selectedColorFamilyNavigationPath(colorFamilyName: string | undefined, familyTypeName: string | undefined): string {
    const transformedFamilyName = this.#urlFormatter.formatStringValueToUrlPath(colorFamilyName);
    const transformedFamilyTypeName = this.#urlFormatter.formatStringValueToUrlPath(familyTypeName);

    const withGrouping = this.#withFamilyTypeGrouping();

    if (withGrouping && familyTypeName && this.#routeFamilyName() && this.#routeFamilyTypeName()) {
      return `../../${transformedFamilyTypeName}/${transformedFamilyName}`;
    } else if (withGrouping && ((familyTypeName && this.#routeFamilyTypeName()) || this.#routeFamilyName())) {
      return `../${transformedFamilyTypeName}/${transformedFamilyName}`;
    } else if (withGrouping && familyTypeName && !this.#routeFamilyTypeName() && !this.#routeFamilyName()) {
      return `${transformedFamilyTypeName}/${transformedFamilyName}`;
    }

    return this.#routeFamilyName() ? `../${transformedFamilyName}` : transformedFamilyName;
  }

  #navigateToPath(navigationPath: string) {
    this.#router.navigate([navigationPath], {
      relativeTo: this.#activatedRoute,
      fragment: this.#routeFamilyName() ? this.familySwitcherId : '',
    });
  }

  #filterOutUndefinedFamily(family: { [x: string]: ColorFamily | undefined } | undefined): boolean {
    return family ? !!Object.values(family)[0] : false;
  }

  #getFamilyObject(family: { [x: string]: ColorFamily | undefined } | undefined): ColorFamily | undefined {
    return family ? Object.values(family)[0] : undefined;
  }

  #withFamilyTypeGrouping(): boolean {
    return Object.keys(this.familiesGroupedByType())?.length > 1;
  }
}
